import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FileCheck } from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { BrandedSpinner } from '@/components/common/BrandedLoading';
import { useQuery } from '@tanstack/react-query';
import { contentApi } from '@/services/adminApi';
import type { Content } from '@/types/admin';

export const GdprPage: React.FC = () => {
  const GDPR_CONTENT_ID = 'CNT-five_ai_gdpr_policy_V1';

  const fallback = useMemo(
    () => ({
      title: 'GDPR & Data Protection',
      description: 'Information about your data protection rights under GDPR and India\'s DPDP Act 2023.',
      lastUpdated: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      sections: [
        {
          id: 'lawful-basis',
          title: '1. Lawful Basis for Processing',
          content:
            'We process personal data on one or more of the following legal bases:\n\n- **Contract**: Processing necessary to deliver the services you signed up for (AI coaching, learning paths, skill assessments)\n- **Consent**: Where we explicitly ask for your permission (e.g., marketing communications, optional analytics)\n- **Legitimate interests**: Product improvement, security, fraud prevention, and analytics, balanced against your rights and freedoms\n- **Legal obligation**: Where processing is required by applicable law (tax, accounting, regulatory compliance)\n\nYou may withdraw consent at any time without affecting the lawfulness of processing prior to withdrawal.',
        },
        {
          id: 'gdpr-rights',
          title: '2. Your GDPR Rights (EEA & UK)',
          content:
            'If you are located in the European Economic Area (EEA) or United Kingdom, the General Data Protection Regulation (GDPR) provides you with the following rights:\n\n- **Right of Access** (Art. 15): Obtain confirmation of whether your data is being processed and receive a copy\n- **Right to Rectification** (Art. 16): Correct inaccurate or incomplete personal data\n- **Right to Erasure** (Art. 17): Request deletion of your personal data ("right to be forgotten")\n- **Right to Restriction** (Art. 18): Request limitation of processing in certain circumstances\n- **Right to Data Portability** (Art. 20): Receive your data in a structured, commonly used, machine-readable format\n- **Right to Object** (Art. 21): Object to processing based on legitimate interests or direct marketing\n- **Right regarding Automated Decision-Making** (Art. 22): Not be subject to decisions based solely on automated processing that produce legal or significant effects\n\nTo exercise these rights, contact our Data Protection Officer at **dpo@fiveai.co**. We will respond within 30 days. You also have the right to lodge a complaint with your local supervisory authority.',
        },
        {
          id: 'dpdp-rights',
          title: '3. Your Rights Under India\'s DPDP Act 2023',
          content:
            'If you are located in India, the Digital Personal Data Protection (DPDP) Act, 2023 provides you with the following rights as a Data Principal:\n\n- **Right to Access Information**: Obtain a summary of your personal data being processed and the processing activities\n- **Right to Correction and Erasure**: Request correction of inaccurate data, completion of incomplete data, updating of outdated data, and erasure of data no longer necessary for the stated purpose\n- **Right to Grievance Redressal**: Register a grievance with us regarding processing of your personal data\n- **Right to Nominate**: Nominate another individual to exercise your rights in the event of your death or incapacity\n\n**Our Obligations as Data Fiduciary**: We provide clear notice about the purpose of data collection, obtain consent before processing, maintain accuracy and security of your data, delete data when consent is withdrawn or the purpose is fulfilled, and implement reasonable security safeguards.\n\nTo exercise your rights under the DPDP Act, contact us at **dpo@fiveai.co** or **support@fiveai.co**.',
        },
        {
          id: 'data-transfers',
          title: '4. International Data Transfers',
          content:
            'Your personal data may be transferred to and processed in countries outside your jurisdiction. We ensure adequate protection through:\n\n- **Standard Contractual Clauses (SCCs)**: EU-approved contractual safeguards for transfers outside the EEA\n- **Adequacy Decisions**: Transfers to countries recognized by the European Commission as providing adequate protection\n- **DPDP Act Compliance**: For Indian users, transfers are made only to jurisdictions permitted under the DPDP Act or with appropriate contractual safeguards\n- **Data Processing Agreements**: All service providers sign agreements ensuring equivalent levels of data protection\n\nOur primary data processing occurs in AWS regions. You may request details of the safeguards in place for your data transfers by contacting **dpo@fiveai.co**.',
        },
        {
          id: 'data-retention',
          title: '5. Data Retention',
          content:
            'We retain personal data only as long as necessary for the purposes described in our Privacy Policy:\n\n- **Account data**: For the duration of your active account plus 90 days after closure for recovery purposes\n- **Transaction records**: As required by tax and accounting laws (typically 7 years under Indian law)\n- **Usage analytics**: Anonymized and aggregated data retained indefinitely for product improvement\n- **Consent records**: Retained for as long as the consent is valid, plus any legally required retention period\n- **Security logs**: Retained for up to 12 months for incident investigation\n\nUpon expiry of the retention period, data is securely deleted or irreversibly anonymized.',
        },
        {
          id: 'data-controller',
          title: '6. Data Controller Information',
          content:
            '**Data Controller / Data Fiduciary**:\nUnboundXLabs Private Limited\nBangalore 560068, India\n\n**Data Protection Officer**:\nEmail: dpo@fiveai.co\n\nFor EEA/UK users, our DPO serves as the primary point of contact for data protection inquiries and supervisory authority communications.\n\nFor Indian users, our DPO also functions as the designated grievance officer under the DPDP Act, 2023.',
        },
        {
          id: 'ai-processing',
          title: '7. AI and Automated Processing',
          content:
            'Our services use artificial intelligence and machine learning to provide personalized coaching, skill assessments, and learning recommendations. Here is how we handle AI-related data processing:\n\n- **Profiling**: We use your learning activity and stated goals to generate personalized recommendations. This profiling does not produce legal or similarly significant effects\n- **AI Model Training**: We do not use your identifiable personal data to train third-party AI models. Our own model fine-tuning uses aggregated, anonymized data\n- **Transparency**: You can request an explanation of how AI-driven recommendations are generated for your profile\n- **Human Oversight**: Critical decisions affecting your account (suspension, billing disputes) always involve human review\n\nIf you believe an automated decision has unfairly affected you, contact **support@fiveai.co** to request human review.',
        },
        {
          id: 'breach-notification',
          title: '8. Data Breach Notification',
          content:
            'In the event of a personal data breach that is likely to result in a risk to your rights and freedoms:\n\n- **GDPR**: We will notify the relevant supervisory authority within 72 hours of becoming aware of the breach, and notify affected individuals without undue delay where the breach is likely to result in a high risk\n- **DPDP Act**: We will notify the Data Protection Board of India and affected Data Principals as required under the Act\n\nOur incident response procedures include immediate containment, forensic investigation, impact assessment, and remediation. We maintain a breach register documenting all incidents and actions taken.',
        },
        {
          id: 'cookies',
          title: '9. Cookies and Consent',
          content:
            'We obtain your consent before placing non-essential cookies, in compliance with GDPR and ePrivacy requirements. You can manage your preferences at any time through our cookie consent banner or your browser settings.\n\nEssential cookies required for authentication and security are placed without consent as they are strictly necessary for the service to function. For more details, see our Cookie Policy.',
        },
        {
          id: 'contact',
          title: '10. Contact and Grievance Redressal',
          content:
            'For any data protection queries, rights requests, or grievances:\n\n**UnboundXLabs Private Limited**\nBangalore 560068, India\n\n- **Data Protection Officer / Grievance Officer**: dpo@fiveai.co\n- **General Support**: support@fiveai.co\n- **Legal Inquiries**: legal@fiveai.co\n\n**Response times**:\n- GDPR rights requests: Within 30 days (extendable by 60 days for complex requests)\n- DPDP Act grievances: As prescribed by the Data Protection Board of India\n\n**Supervisory Authorities**:\n- EEA/UK: You may lodge a complaint with your local data protection authority\n- India: Data Protection Board of India (once fully operational under the DPDP Act)',
        },
      ],
    }),
    []
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-gdpr', GDPR_CONTENT_ID],
    queryFn: () => contentApi.getByContentId(GDPR_CONTENT_ID),
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  const isRecord = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

  const resolved = useMemo(() => {
    const content: Content | null = data ?? null;
    const metadata = content?.metadata;
    const gdprContent = isRecord(metadata) && isRecord(metadata.gdprContent) ? metadata.gdprContent : null;
    const intro = gdprContent && isRecord(gdprContent.introduction) ? gdprContent.introduction : null;
    const sectionsRaw = gdprContent && Array.isArray(gdprContent.sections) ? (gdprContent.sections as unknown[]) : [];
    const footer = gdprContent && isRecord(gdprContent.footer) ? gdprContent.footer : null;

    const lastUpdated =
      isRecord(metadata) && typeof metadata.lastUpdated === 'string'
        ? metadata.lastUpdated
        : content?.updatedAt
          ? new Date(content.updatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
          : fallback.lastUpdated;

    const sections =
      sectionsRaw
        .map((s, idx) => {
          const sec = isRecord(s) ? s : null;
          const id = sec && typeof sec.id === 'string' ? sec.id : `section-${idx + 1}`;
          const title = sec && typeof sec.title === 'string' ? sec.title : `Section ${idx + 1}`;
          const contentText = sec && typeof sec.content === 'string' ? sec.content : '';
          return { id, title, content: contentText };
        })
        .filter((s) => s.title || s.content) || [];

    const headerTitle = content?.title || fallback.title;
    const headerDescription = content?.description || fallback.description;

    const introTitle = intro && typeof intro.title === 'string' ? intro.title : undefined;
    const introText = intro && typeof intro.text === 'string' ? intro.text : undefined;

    const footerTitle = footer && typeof footer.title === 'string' ? footer.title : undefined;
    const footerContent = footer && typeof footer.content === 'string' ? footer.content : undefined;

    return {
      isFallback: !content || !gdprContent,
      headerTitle,
      headerDescription,
      lastUpdated,
      introTitle,
      introText,
      sections: sections.length > 0 ? sections : fallback.sections,
      footerTitle,
      footerContent,
    };
  }, [data, fallback]);

  useEffect(() => {
    document.title = resolved.headerTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && resolved.headerDescription) {
      meta.setAttribute('content', resolved.headerDescription);
    }
  }, [resolved.headerTitle, resolved.headerDescription]);

  const renderInline = (text: string) => {
    const boldParts = text.split(/\*\*(.*?)\*\*/g);
    return boldParts.map((part, idx) => {
      const isBold = idx % 2 === 1;
      const urlParts = part.split(/(https?:\/\/[^\s)]+)\b/g);
      const nodes = urlParts.map((p, j) => {
        const isUrl = /^https?:\/\//.test(p);
        if (!isUrl) return <React.Fragment key={`${idx}-${j}`}>{p}</React.Fragment>;
        return (
          <a
            key={`${idx}-${j}`}
            href={p}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline break-all"
          >
            {p}
          </a>
        );
      });
      return isBold ? <strong key={idx}>{nodes}</strong> : <span key={idx}>{nodes}</span>;
    });
  };

  return (
    <main id="main-content" className="min-h-screen bg-background" role="main" aria-label="GDPR & Data Protection">
      <Header />
      <div className="pt-16">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.18),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_20%,hsl(263_70%_50%/0.08),transparent)]" />
          <div className="container relative mx-auto px-4 py-24 sm:py-32">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8"
              >
                <FileCheck className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Compliance</span>
              </motion.div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
                <span className="text-foreground">GDPR &</span>
                <br />
                <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
                  Data Protection
                </span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-4">
                {resolved.headerDescription || 'Information about your data protection rights under GDPR and India\'s DPDP Act 2023.'}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-muted-foreground/80">
                <span>Last updated: {resolved.lastUpdated}</span>
                {isLoading && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">
                    <BrandedSpinner sizeClassName="h-3 w-3" />
                    Loading...
                  </span>
                )}
                {isError && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 text-xs">
                    Showing fallback
                  </span>
                )}
                {resolved.isFallback && !isLoading && !isError && (
                  <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs">
                    Default content
                  </span>
                )}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-4 sm:py-8">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid gap-8 lg:grid-cols-[1fr_240px]">
              <div className="min-w-0">
                {(resolved.introTitle || resolved.introText) && (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mb-10"
                  >
                    <Card className="rounded-2xl border-2 bg-gradient-to-br from-primary/5 to-violet-500/5 p-6 sm:p-8">
                      {resolved.introTitle && (
                        <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-3">
                          {resolved.introTitle}
                        </h2>
                      )}
                      {resolved.introText && (
                        <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                          {renderInline(resolved.introText)}
                        </div>
                      )}
                    </Card>
                  </motion.div>
                )}

                <div className="space-y-10">
                  {resolved.sections.map((s, i) => (
                    <motion.section
                      key={s.id}
                      id={s.id}
                      className="scroll-mt-28"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-50px' }}
                      transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.3) }}
                    >
                      <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-3">
                        {s.title}
                      </h2>
                      <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                        {renderInline(s.content)}
                      </div>
                    </motion.section>
                  ))}
                </div>

                {(resolved.footerTitle || resolved.footerContent) && (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-10"
                  >
                    <Card className="rounded-2xl border-2 bg-muted/30 p-6 sm:p-8">
                      {resolved.footerTitle && (
                        <h2 className="text-xl font-bold text-foreground tracking-tight mb-3">
                          {resolved.footerTitle}
                        </h2>
                      )}
                      {resolved.footerContent && (
                        <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                          {renderInline(resolved.footerContent)}
                        </div>
                      )}
                    </Card>
                  </motion.div>
                )}
              </div>

              <aside className="hidden lg:block">
                <div className="sticky top-24">
                  <Card className="rounded-2xl border-2 p-6 bg-card">
                    <CardHeader className="p-0 mb-4">
                      <CardTitle className="text-base font-semibold text-foreground">On this page</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <nav className="space-y-2" aria-label="GDPR sections">
                        {resolved.sections.map((s) => (
                          <a
                            key={s.id}
                            href={`#${s.id}`}
                            className="block text-sm text-muted-foreground hover:text-primary hover:underline transition-colors py-0.5"
                          >
                            {s.title}
                          </a>
                        ))}
                      </nav>
                    </CardContent>
                  </Card>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
};
